import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Stack,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../theme/BrandColorContext';
import api from '../api/client';

function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { login } = useAuth();
  const { setBranding } = useBranding();
  const token = searchParams.get('token') || '';
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [employeeOnly, setEmployeeOnly] = useState(false);

  useEffect(() => {
    setBranding({
      primaryColor: null,
      accentColor: null,
      fontFamily: null,
      fontUrl: null,
      logo: null,
      slogan: null,
      fontSizeScale: 1,
    });
  }, [setBranding]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Hasło musi mieć co najmniej 8 znaków.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Hasła nie są takie same.');
      return;
    }
    try {
      const account = await api.post('/api/auth/accept-invite', {
        token,
        password,
        displayName: displayName.trim() || undefined,
      });
      if (account.user.role === 'employee') {
        // Employees use mobile app; do not log into web panel
        setEmployeeOnly(true);
        return;
      }
      login(account);
      setSent(true);
    } catch (err) {
      setError(err.body?.error || err.message || 'Nie udało się aktywować konta.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: theme.tokens?.semantic?.surface?.canvas ?? '#F7F6F3',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 440, borderRadius: `${theme.tokens?.primitive?.radius?.hero ?? 30}px` }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 750, fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
            Aktywuj konto
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Ustaw hasło, aby dokończyć rejestrację.
          </Typography>
          {employeeOnly ? (
            <Alert severity="info">
              Konto pracownika zostało aktywowane. Zaloguj się w aplikacji mobilnej.
            </Alert>
          ) : sent ? (
            <Alert severity="success">Konto aktywowane. Przekierowanie...</Alert>
          ) : (
            <form onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                {error && <Alert severity="error">{error}</Alert>}
                {!token && <Alert severity="error">Brak tokenu zaproszenia.</Alert>}
                <TextField
                  label="Imię i nazwisko (opcjonalne)"
                  fullWidth
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
                <TextField
                  label="Hasło"
                  type="password"
                  fullWidth
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <TextField
                  label="Powtórz hasło"
                  type="password"
                  fullWidth
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <Button type="submit" variant="contained" fullWidth size="large">
                  Aktywuj konto
                </Button>
              </Stack>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default AcceptInvite;
